import type { UpcomingSession } from '@/lib/api/trainer';

export interface DateGroup {
  dateKey: string;
  label: string;
  isToday: boolean;
  sessions: UpcomingSession[];
}

export function groupSessionsByDate(sessions: UpcomingSession[]): DateGroup[] {
  const todayKey = new Date().toLocaleDateString('en-CA');
  const map = new Map<string, UpcomingSession[]>();

  for (const s of sessions) {
    const key = new Date(s.scheduled_at).toLocaleDateString('en-CA');
    const existing = map.get(key);
    if (existing) existing.push(s);
    else map.set(key, [s]);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => {
      const [y, m, d] = key.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      return {
        dateKey: key,
        label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        isToday: key === todayKey,
        sessions: items,
      };
    });
}

export function findModule<M extends { title: string; topics: { id: string }[] }>(
  modules: M[],
  topicId: string,
): M | undefined {
  return modules.find((m) => m.topics.some((t) => t.id === topicId));
}

export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function isPastOrToday(scheduledAt: string): boolean {
  const todayKey = new Date().toLocaleDateString('en-CA');
  const key = new Date(scheduledAt).toLocaleDateString('en-CA');
  return key <= todayKey;
}

export function mostUrgentUnmarkedSession(sessions: UpcomingSession[]): UpcomingSession | undefined {
  const unmarked = sessions.filter((s) => isPastOrToday(s.scheduled_at) && !s.attendance_taken);
  if (unmarked.length === 0) return undefined;
  return unmarked.reduce((latest, s) =>
    new Date(s.scheduled_at).getTime() > new Date(latest.scheduled_at).getTime() ? s : latest,
  );
}
